/**
 * Image URL sanitiser.
 *
 * Mirrors site_helpers/image_url_utils.js in tf-express.
 *
 * Repairs malformed cdn.trailfinders.com image URLs:
 *   - Collapses double "?" between query parts
 *     (e.g. ?width=100?height=600 becomes ?width=100&height=600).
 *   - Dedupes duplicate keys (last value wins).
 *   - Normalises ?w= / ?h= aliases to ?width= / ?height=.
 *   - Preserves unrelated params (format, quality, signed tokens, etc).
 *   - Preserves the URL fragment (#anchor).
 *
 * Host-gated: only cdn.trailfinders.com (and protocol-relative
 * //cdn.trailfinders.com) is touched. Everything else passes through
 * unchanged so non-CDN URLs keep whatever params they already had.
 *
 * Idempotent: sanitizeImageUrl(sanitizeImageUrl(x)) === sanitizeImageUrl(x).
 */

export function formatLabel(label: string): string {
  return label
    .split(' ')
    .map((word, i) => {
      if (word.toLowerCase() === 'and') return '&'
      if (i !== 0 && word.toLowerCase() === 'the') return 'the'
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

export function getCurrencySymbol(): string {
  return window.siteVar?.currency_symbol
    ?? (window.siteVar?.referer === 'IE' ? '€' : '£')
}

const CDN_HOST = 'cdn.trailfinders.com'

const KEY_ALIASES: Record<string, string> = {
  w: 'width',
  h: 'height',
}

function isCdnUrl(url: string): boolean {
  if (url.startsWith('//')) {
    return url.slice(2).toLowerCase().startsWith(CDN_HOST)
  }
  const match = url.match(/^https?:\/\/([^/]+)/i)
  return !!match && match[1].toLowerCase() === CDN_HOST
}

function splitFragment(str: string): [string, string] {
  const hashIdx = str.indexOf('#')
  if (hashIdx === -1) return [str, '']
  return [str.slice(0, hashIdx), str.slice(hashIdx + 1)]
}

function splitPathAndQuery(str: string): [string, string] {
  const parts = str.split('?')
  const path = parts.shift() ?? ''
  const queryChunks = parts.filter((chunk) => chunk.length > 0)
  return [path, queryChunks.join('&')]
}

export type ImageUrlParams = Record<string, string | number | null | undefined>

export function sanitizeImageUrl(url: string | null | undefined, params?: ImageUrlParams): string {
  if (!url) return ''
  if (typeof url !== 'string') return url
  if (!isCdnUrl(url)) return url

  const [withoutFragment, fragment] = splitFragment(url)
  const [path, rawQuery] = splitPathAndQuery(withoutFragment)

  const search = new URLSearchParams(rawQuery)

  const out = new URLSearchParams()
  for (const [key, value] of search.entries()) {
    const normalizedKey = KEY_ALIASES[key] ?? key
    out.set(normalizedKey, value)
  }

  if (params && typeof params === 'object') {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue
      const normalizedKey = KEY_ALIASES[key] ?? key
      out.set(normalizedKey, String(value))
    }
  }

  const queryString = out.toString()
  let result = path
  if (queryString) result += '?' + queryString
  if (fragment) result += '#' + fragment
  return result
}
